'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export interface UserMessage {
  id?: string;
  userProfileId: string;
  subject: string;
  message: string;
  senderType: 'user' | 'admin';
  senderId?: string;
  isRead?: boolean;
  readAt?: string;
  priority?: string;
  parentMessageId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const userMessageService = {
  async sendMessage(messageData: UserMessage) {
    const supabase = createClient();

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_messages')
        .insert({
          user_profile_id: messageData.userProfileId,
          subject: messageData.subject,
          message: messageData.message,
          sender_type: messageData.senderType,
          sender_id: user.id,
          priority: messageData.priority || 'normal',
          parent_message_id: messageData.parentMessageId,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return {
        id: data.id,
        userProfileId: data.user_profile_id,
        subject: data.subject,
        message: data.message,
        senderType: data.sender_type,
        senderId: data.sender_id,
        isRead: data.is_read,
        priority: data.priority,
        parentMessageId: data.parent_message_id,
        createdAt: data.created_at,
      };
    } catch (error: any) {
      throw error;
    }
  },

  async getMessagesByProfileId(userProfileId: string): Promise<UserMessage[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_messages')
        .select('*')
        .eq('user_profile_id', userProfileId)
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return data.map((msg: any) => ({
        id: msg.id,
        userProfileId: msg.user_profile_id,
        subject: msg.subject,
        message: msg.message,
        senderType: msg.sender_type,
        senderId: msg.sender_id,
        isRead: msg.is_read,
        readAt: msg.read_at,
        priority: msg.priority,
        parentMessageId: msg.parent_message_id,
        createdAt: msg.created_at,
        updatedAt: msg.updated_at,
      }));
    } catch (error: any) {
      throw error;
    }
  },

  async markAsRead(messageId: string) {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_messages')
        .update({
          is_read: true,
          read_at: new Date().toISOString(),
        })
        .eq('id', messageId)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return data;
    } catch (error: any) {
      throw error;
    }
  },

  async getUnreadCount(userProfileId: string): Promise<number> {
    const supabase = createClient();

    try {
      const { count, error } = await supabase
        .from('user_messages')
        .select('*', { count: 'exact', head: true })
        .eq('user_profile_id', userProfileId)
        .eq('is_read', false)
        .eq('sender_type', 'admin');

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return count || 0;
    } catch (error: any) {
      throw error;
    }
  },
};